<script setup>
// fork overlay: fork-owned component (FEAT-002 + EXT-001, issue #18) — no OSS counterpart.
// One-off API campaign form. Mirrors the OSS SMSCampaignForm (free-text message)
// and adds the EXT-001 send-delay block + the 150k message ceiling.
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  requiredIf,
  minLength,
  maxLength,
  numeric,
  minValue,
  maxValue,
} from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const MESSAGE_MAX_LENGTH = 150000;
const DELAY_MIN = '0';
const DELAY_MAX = '300';

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  // No dedicated getAPIInboxes getter exists upstream; filter inline to avoid
  // overlaying the inboxes store.
  inboxes: useMapGetter('inboxes/getInboxes'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  delayType: 'none',
  delaySeconds: 5,
  delayMin: 3,
  delayMax: 10,
};

const state = reactive({ ...initialState });

const isFixedDelay = computed(() => state.delayType === 'fixed');
const isRandomDelay = computed(() => state.delayType === 'random');

// EXT-001: the delay range validators must only apply on the active delay path.
// requiredIf already gates the "required" rule, but numeric/minValue/maxValue
// were ungated, so a stale out-of-range value on a now-hidden delay field (e.g.
// after switching delayType back to 'none') kept v$.$invalid true and
// dead-disabled the submit button with no editable field to correct. Gate the
// range rules on the same condition so inactive delay fields never block submit.
const onlyWhen = (active, rule) => value => !active() || rule(value);
const whenFixed = () => state.delayType === 'fixed';
const whenRandom = () => state.delayType === 'random';

const rules = {
  title: { required, minLength: minLength(1) },
  message: {
    required,
    minLength: minLength(1),
    maxLength: maxLength(MESSAGE_MAX_LENGTH),
  },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
  delaySeconds: {
    requiredIfFixed: requiredIf(whenFixed),
    numeric: onlyWhen(whenFixed, numeric),
    minValue: onlyWhen(whenFixed, minValue(0)),
    maxValue: onlyWhen(whenFixed, maxValue(300)),
  },
  delayMin: {
    requiredIfRandom: requiredIf(whenRandom),
    numeric: onlyWhen(whenRandom, numeric),
    minValue: onlyWhen(whenRandom, minValue(0)),
    maxValue: onlyWhen(whenRandom, maxValue(300)),
  },
  delayMax: {
    requiredIfRandom: requiredIf(whenRandom),
    numeric: onlyWhen(whenRandom, numeric),
    minValue: onlyWhen(whenRandom, minValue(0)),
    maxValue: onlyWhen(whenRandom, maxValue(300)),
    gteMin: value => !whenRandom() || Number(value) >= Number(state.delayMin),
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const apiInboxes = computed(() =>
  (formState.inboxes.value ?? []).filter(
    inbox => inbox.channel_type === INBOX_TYPES.API
  )
);

const inboxOptions = computed(() => mapToOptions(apiInboxes.value, 'id', 'name'));

const delayTypeOptions = computed(() => [
  { value: 'none', label: t('CAMPAIGN.API.CREATE.FORM.DELAY.TYPES.NONE') },
  { value: 'fixed', label: t('CAMPAIGN.API.CREATE.FORM.DELAY.TYPES.FIXED') },
  { value: 'random', label: t('CAMPAIGN.API.CREATE.FORM.DELAY.TYPES.RANDOM') },
]);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.API.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const delayError = computed(() => {
  const invalid =
    v$.value.delaySeconds.$error ||
    v$.value.delayMin.$error ||
    v$.value.delayMax.$error;
  return invalid ? t('CAMPAIGN.API.CREATE.FORM.DELAY.ERROR') : '';
});

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const buildDelayConfig = () => {
  if (state.delayType === 'fixed') {
    return { type: 'fixed', seconds: Number(state.delaySeconds) };
  }
  if (state.delayType === 'random') {
    return { type: 'random', min: Number(state.delayMin), max: Number(state.delayMax) };
  }
  return { type: 'none' };
};

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience: state.selectedAudience?.map(id => ({
    id,
    type: 'Label',
  })),
  trigger_rules: { delay: buildDelayConfig() },
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Exposed for unit tests — script-setup internals are otherwise private.
defineExpose({
  state,
  apiInboxes,
  inboxOptions,
  audienceList,
  buildDelayConfig,
  prepareCampaignDetails,
  isSubmitDisabled,
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.API.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.API.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.API.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.API.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      :max-length="MESSAGE_MAX_LENGTH"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.API.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.API.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.API.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.API.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.API.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.API.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.API.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- EXT-001: configurable inter-message send delay -->
    <div class="flex flex-col gap-1">
      <label for="delayType" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.API.CREATE.FORM.DELAY.LABEL') }}
      </label>
      <ComboBox
        id="delayType"
        v-model="state.delayType"
        :options="delayTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.API.CREATE.FORM.DELAY.INFO') }}
      </p>

      <Input
        v-if="isFixedDelay"
        v-model="state.delaySeconds"
        type="number"
        :min="DELAY_MIN"
        :max="DELAY_MAX"
        :label="t('CAMPAIGN.API.CREATE.FORM.DELAY.SECONDS.LABEL')"
        :placeholder="t('CAMPAIGN.API.CREATE.FORM.DELAY.SECONDS.PLACEHOLDER')"
        class="mt-2"
      />

      <div v-if="isRandomDelay" class="flex gap-3 mt-2">
        <Input
          v-model="state.delayMin"
          type="number"
          :min="DELAY_MIN"
          :max="DELAY_MAX"
          :label="t('CAMPAIGN.API.CREATE.FORM.DELAY.MIN.LABEL')"
          :placeholder="t('CAMPAIGN.API.CREATE.FORM.DELAY.MIN.PLACEHOLDER')"
          class="w-full"
        />
        <Input
          v-model="state.delayMax"
          type="number"
          :min="DELAY_MIN"
          :max="DELAY_MAX"
          :label="t('CAMPAIGN.API.CREATE.FORM.DELAY.MAX.LABEL')"
          :placeholder="t('CAMPAIGN.API.CREATE.FORM.DELAY.MAX.PLACEHOLDER')"
          class="w-full"
        />
      </div>
      <p v-if="delayError" class="mt-1 text-xs text-n-ruby-9">
        {{ delayError }}
      </p>
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.API.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.API.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
